import { ArcaneRecoveryChoices } from './SpellRecoveryControls';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Modal } from 'react-bootstrap';
import { io } from 'socket.io-client';
import apiFetch from '../../../utils/apiFetch';
const requestId = () => window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
const read = key => { try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch { return null; } };
const save = (key, value) => { try { if (value) sessionStorage.setItem(key, JSON.stringify(value)); else sessionStorage.removeItem(key); } catch { /* The current tab can still retry. */ } };
export default function ShortRestRecoveryModal({ campaignId, characterId }) {
  const endpoint = `/campaigns/${encodeURIComponent(campaignId || '')}/rests/recovery/${encodeURIComponent(characterId || '')}`;
  const key = `hit-die-recovery:${campaignId}:${characterId}`;
  const [data, setData] = useState(null), [error, setError] = useState(''), [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(() => read(key));
  const scope = useRef(endpoint), sequence = useRef(0), lock = useRef(false);
  scope.current = endpoint;
  const reload = useCallback(async () => {
    if (!campaignId || !characterId) return;
    const version = ++sequence.current;
    try {
      const response = await apiFetch(endpoint, { cache: 'no-store' });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Unable to load rest recovery.');
      if (version === sequence.current && scope.current === endpoint) { setData(result); setError(''); }
    } catch (failure) { if (version === sequence.current && scope.current === endpoint) setError(failure.message); }
  }, [campaignId, characterId, endpoint]);
  useEffect(() => {
    setData(null); setError(''); setPending(read(key)); reload();
    if (!campaignId || !characterId) return undefined;
    const socket = io(process.env.REACT_APP_API_URL || undefined, { withCredentials: true });
    socket.on('connect', () => { socket.emit('campaign:join', campaignId); reload(); });
    socket.on('campaign:characters:update', update => {
      if (update?.rest) reload();
    });
    window.addEventListener('focus', reload);
    const timer = window.setInterval(reload, 30000);
    return () => { sequence.current++; socket.disconnect(); clearInterval(timer); window.removeEventListener('focus', reload); };
  }, [campaignId, characterId, endpoint, key, reload]);
  const submit = async (action, die, slots) => {
    if (lock.current || !data?.recovery && !pending) return;
    lock.current = true; setBusy(true); setError('');
    const choice = pending || { id: requestId(), restId: data.recovery.id, revision: data.recovery.revision, action, ...(die ? { die } : {}), ...(slots ? { slots } : {}) };
    save(key, choice); setPending(choice); sequence.current++;
    try {
      const response = await apiFetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(choice) });
      const result = await response.json();
      if (!response.ok) {
        if (response.status >= 400 && response.status < 500) { save(key, null); if (scope.current === endpoint) { setPending(null); await reload(); } }
        throw new Error(result.message || 'Unable to save recovery.');
      }
      save(key, null);
      if (scope.current === endpoint) { sequence.current++; setPending(null); setData(result); }
    } catch (failure) { if (scope.current === endpoint) setError(failure.message); }
    finally { lock.current = false; if (scope.current === endpoint) setBusy(false); }
  };
  const show = Boolean(pending || data?.recovery?.status === 'pending');
  const last = data?.recovery?.lastRoll;
  const canHeal = data?.currentHp > 0 && data.currentHp < data.maxHp;
  const pools = data?.pools || [];
  return <Modal show={show} onHide={() => { if (!busy && !pending) submit('finish'); }} backdrop="static" keyboard={false} centered className="dnd-modal modern-modal">
    <Modal.Header><Modal.Title>Short Rest · Spend Hit Dice</Modal.Title></Modal.Header>
    <Modal.Body>
      <p>Your DM granted a Short Rest. Your short-rest resources have recovered. Spend Hit Dice to regain Hit Points, or finish without spending any.</p>
      {data && <p><strong>{data.currentHp} / {data.maxHp} HP</strong> · Constitution {data.constitutionModifier >= 0 ? '+' : ''}{data.constitutionModifier}</p>}
      {last && <Alert variant="success">d{last.die}: {last.roll}{last.heroicInspiration ? ` (rerolled from ${last.heroicInspiration.from} with Heroic Inspiration)` : ''} {last.modifier >= 0 ? '+' : '−'} {Math.abs(last.modifier)} → {last.healing} healing. Regained {last.actualHealing} HP.
        {/* Heroic Inspiration: reroll the Hit Die just rolled; the new roll is used. */}
        {!last.heroicInspiration && data?.heroicInspiration?.has === true && !pending && <div className="mt-2">
          <Button size="sm" variant="outline-dark" disabled={busy} onClick={() => submit('heroic-inspiration')}>Reroll the d{last.die} · Heroic Inspiration</Button></div>}
      </Alert>}
      {error && <Alert variant="danger">{error}</Alert>}
      {pending ? <Alert variant="info">A choice is awaiting confirmation. Retry safely to confirm the original result.</Alert> : <>
        <p>Roll one die, then choose whether to spend another.</p>
        <div className="d-flex flex-wrap gap-2">
          {pools.map(pool => <Button key={pool.die} disabled={busy || !canHeal || pool.remaining < 1} onClick={() => submit('spend', pool.die)}>Spend d{pool.die} · {pool.remaining} remaining</Button>)}
        </div>
        <ArcaneRecoveryChoices key={data?.recovery?.id} profile={data?.spellRecovery?.arcane} busy={busy} onRecover={slots => submit('arcane-recovery', undefined, slots)} />
        <ArcaneRecoveryChoices key={`natural-${data?.recovery?.id}`} name="Natural Recovery" profile={data?.spellRecovery?.natural} busy={busy} onRecover={slots => submit('natural-recovery', undefined, slots)} />
        {!pools.some(pool => pool.remaining > 0) && <p className="mt-2">No Hit Dice available.</p>}
        {data?.currentHp >= data?.maxHp && <p className="mt-2">You are at full Hit Points.</p>}
      </>}
    </Modal.Body>
    <Modal.Footer>{pending ? <Button disabled={busy} onClick={() => submit()}>{busy ? 'Saving…' : 'Retry choice'}</Button> : <Button variant="secondary" disabled={busy} onClick={() => submit('finish')}>Finish recovery</Button>}</Modal.Footer>
  </Modal>;
}
